import {
  type InitialProfileInfoReq,
  OrganizationType,
  type SportResponseRecord,
  type UpdateProfileSearchInfoReqBody,
  v1ProfileInfoCollectInitialCreate,
  v1ProfileInfoNUpdateProfileSearchUpdate,
} from '@on3/api';
import { type OptionType } from '@on3/ui-lib/components/AutoComplete/AutoComplete';
import { OrganizationAutoComplete } from '@on3/ui-lib/components/AutoComplete/OrganizationAutoComplete';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { SocialIcon } from '@on3/ui-lib/components/SocialIcon';
import { externalApi } from '@on3/ui-lib/index';
import { recruitsSubConfig } from '@on3/ui-lib/utils/constants';
import { filterPhoneNumber } from '@on3/ui-lib/utils/filters';
import { formatPhoneNumber } from '@on3/ui-lib/utils/formatters';
import { generateKey } from '@on3/ui-lib/utils/generateKey';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { yup } from '@on3/ui-lib/utils/yup';
import { useGetRecruitedForm } from 'components/GetRecruited/SearchPage/Form';
import { SearchPageFeatures } from 'components/GetRecruited/SearchPage/Form/Partials/Features';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import styles from './Collection.module.scss';

type ProfileInfoReq = InitialProfileInfoReq | UpdateProfileSearchInfoReqBody;

const schema = yup.object().shape({
  firstName: yup.string().required("Athlete's first name is required"),
  lastName: yup.string().required("Athlete's last name is required"),
  instagram: yup.string(),
  phone: yup.string(),
  email: yup.string().email().required('Email address is required'),
  highSchoolClassYear: yup
    .string()
    .required('High school graduation year is required'),
  highSchoolOrganizationKey: yup.number().nullable(),
  highSchoolSearch: yup.string(),
  highSchoolFound: yup.boolean().default(false),
  sportKey: yup
    .number()
    .transform((s) => Number(s) || null)
    .nullable()
    .label('Sport'),
});

interface CollectionProps {
  sports: SportResponseRecord[];
}

export const Collection = ({ sports }: CollectionProps) => {
  const router = useRouter();
  const sportQuery = router.query.sport;
  const { info } = useGetRecruitedForm();

  const sportOptions = (
    sports ?? [
      {
        label: 'Football',
        value: '1',
        slug: 'football',
      },
    ]
  )
    .map((sport) => ({
      label: sport?.name || '',
      value: String(sport?.key),
      slug: slugify(sport?.name),
    }))
    .concat([{ label: 'Other', value: '', slug: 'other' }]);

  const [values, setValues] = useState<InitialProfileInfoReq>({
    firstName: '',
    lastName: '',
    instagram: '',
    cell: '',
    email: '',
    highSchoolClassYear: new Date().getFullYear(),
    highSchoolOrganizationKey: null,
    highSchoolSearch: '',
    highSchoolFound: false,
    sportKey:
      Number(sportOptions.find((so) => so.slug === sportQuery)?.value) || 0,
    ...(info?.collectedProfileInfo ?? {}),
  });

  const missingRequired =
    !values.firstName || !values.lastName || !values.email;

  const handleValue = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSchoolChange = (value: OptionType[]) => {
    setValues((prev) => ({
      ...prev,
      highSchoolOrganizationKey: value[0]?.key,
      highSchoolFound: value.length > 0,
    }));
  };

  const handleSubmit = useCallback(
    async (data: ProfileInfoReq) => {
      try {
        const profileData = info?.collectedProfileInfo
          ? await v1ProfileInfoNUpdateProfileSearchUpdate(
              externalApi,
              info.collectedProfileInfo.key,
              info.collectedProfileInfo.nonce,
              data,
            )
          : await v1ProfileInfoCollectInitialCreate(externalApi, data);

        router.push({
          pathname: '/recruits/search/',
          query: {
            key: profileData.collectedProfileInfo.key,
            nonce: profileData.collectedProfileInfo.nonce,
            rk: generateKey(),
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [router, info?.collectedProfileInfo],
  );

  return (
    <div className={styles.block}>
      <Form
        className={styles.form}
        onSubmit={handleSubmit}
        schema={schema}
        values={values}
      >
        {({ errors, processing }) => (
          <div className={styles.fields}>
            <Input
              error={errors.firstName}
              label="Athlete First Name"
              name="firstName"
              onChange={handleValue}
              required
              type="text"
              value={values.firstName}
            />
            <Input
              error={errors.lastName}
              label="Athlete Last Name"
              name="lastName"
              onChange={handleValue}
              required
              type="text"
              value={values.lastName}
            />
            <Input
              label="Instagram"
              name="instagram"
              onChange={handleValue}
              suffix={<SocialIcon type="instagram" />}
              type="text"
              value={values.instagram ?? ''}
            />
            <Input
              label="Cell Number"
              name="cell"
              onChange={({ target }) =>
                setValues({ ...values, cell: filterPhoneNumber(target.value) })
              }
              type="tel"
              value={formatPhoneNumber(values.cell as string)}
            />
            <Input
              error={errors.email}
              label="Email Address"
              name="email"
              onChange={handleValue}
              required
              type="email"
              value={values.email}
            />
            <Input
              error={errors.highSchoolClassYear}
              inputMode="numeric"
              label="High School Graduation Year"
              name="highSchoolClassYear"
              onChange={handleValue}
              required
              type="text"
              value={values.highSchoolClassYear}
            />
            <Select
              error={errors.sportKey}
              label="Sport"
              name="sportKey"
              onChange={handleValue}
              options={sportOptions}
              placeholder="Select a sport"
              required
              value={String(values.sportKey ?? '')}
            />
            <OrganizationAutoComplete
              id="highSchool"
              label="High School"
              limit={1}
              name="highSchool"
              onChange={handleSchoolChange}
              onSearch={(v) => setValues({ ...values, highSchoolSearch: v })}
              type={OrganizationType.HighSchool}
              value={[
                {
                  key: info?.highSchoolOrganization?.key || 0,
                  label: info?.highSchoolOrganization?.fullName || '',
                  value: info?.highSchoolOrganization?.key,
                },
              ].filter((o) => o.key)}
            />
            <button disabled={processing || missingRequired} type="submit">
              Continue
            </button>
          </div>
        )}
      </Form>

      <div className={styles.marketing}>
        <div className={styles.pricing}>
          <span>${recruitsSubConfig.pricing.base}</span>
          <span>
            ${recruitsSubConfig.pricing.offer}/
            {recruitsSubConfig.pricing.period}
          </span>
          <span>{recruitsSubConfig.pricing.discount} off</span>
        </div>
        <SearchPageFeatures />
      </div>
    </div>
  );
};
